import { captureException } from "@sentry/vue";

import type { ActionPerformed } from "@capacitor/push-notifications";
import { APP_CHAT_ROUTES, APP_SCHEDULE_ROUTES, APP_ROUTES } from "@/Generated/Routes/paths";
import {
  type NOTIFICATION_DATA,
  type CHAT_PAYLOAD,
  type VIDEO_CALL_PAYLOAD,
  NotificationType,
} from "../interfaces";

import { useChatStore, type ChatStore } from "@/Modules/Chat/Store";
import { useAuthStore, type AuthStore } from "@/Modules/Auth/Store";
import { useUserStore, type UserStore } from "@/Modules/User/Store";
import { useVideoCallStore, type VideoCallStore } from "@/Modules/VideoCall/Store";
import AppRouter from "@/Router";

import { isValidString } from "@/Utils/String";
import { danger, log } from "@/Utils/Debug";
import { hasValue, isObject } from "@/Utils/Objects";
import { waitFor } from "@/Utils/Promises";

class PerformNotificationActionHandler {
  constructor(
    private readonly chatStore: ChatStore = useChatStore(),
    private readonly authStore: AuthStore = useAuthStore(),
    private readonly userStore: UserStore = useUserStore(),
    private readonly videoCallStore: VideoCallStore = useVideoCallStore()
  ) {}

  public handle(action: ActionPerformed): Promise<boolean> {
    try {
      if (!this.authStore.isUserLogged) {
        return Promise.resolve(false);
      } else if (!hasValue(action) || !hasValue(action.notification) || !isObject(action.notification.data)) {
        return Promise.resolve(false);
      }

      const payload = action.notification.data as NOTIFICATION_DATA;
      const type = `${payload.type}`;

      if (type === NotificationType.CHATMSG) {
        return this._resolveMSG(payload as CHAT_PAYLOAD);
      } else if (type === NotificationType.VIDEO_CALL) {
        return this._resolveVideoCall(payload as VIDEO_CALL_PAYLOAD);
      } else if (type === NotificationType.ACTIVITY_CENTER_EVENT) {
        return Promise.resolve(this._resolveActivityCenter());
      } else if (type === NotificationType.THERAPIST_ASSIGNED) {
        return Promise.resolve(this._resolveTherapistAssigned());
      } else if (type === NotificationType.ASSIGMENTS_USER_ADDED) {
        return Promise.resolve(this._resolveAssigmentsUserAdded());
      } else if (type === NotificationType.SCHEDULE_REMINDER) {
        return Promise.resolve(this._resolveScheduleReminder());
      }

      return Promise.resolve(false);
    } catch (e) {
      danger("Error perform notification", action);

      captureException(e);
      return Promise.resolve(false);
    }
  }

  private _selectChat(chatId?: string): boolean {
    if (!isValidString(chatId) || !hasValue(this.chatStore.rooms[chatId])) {
      this.chatStore.activeChatRoomId = undefined;

      AppRouter.push({ name: APP_CHAT_ROUTES.root.name });

      return false;
    } else if (this.chatStore.activeChatRoomId === chatId) {
      AppRouter.push({ name: APP_CHAT_ROUTES.root.name });

      return true;
    }

    this.chatStore.setActiveChatRoom(chatId);
    AppRouter.push({ name: APP_CHAT_ROUTES.root.name });

    return true;
  }

  private async _resolveMSG(payload: CHAT_PAYLOAD): Promise<boolean> {
    await waitFor(0.5);

    return this._selectChat(this._getChatId(payload));
  }

  private async _resolveVideoCall(payload: VIDEO_CALL_PAYLOAD): Promise<boolean> {
    log("Push video_call");
    const chatId = this._getChatId(payload);
    const isSelected = this._selectChat(chatId);

    log("Is selected Chat", isSelected, chatId);
    if (this.userStore.getUserIsPatient && isSelected) {
      return this.videoCallStore.checkVideoCallInProgress(chatId);
    } else {
      return false;
    }
  }

  private _resolveActivityCenter(): boolean {
    AppRouter.push({ name: APP_ROUTES.activity.root.name });

    return true;
  }

  private _resolveTherapistAssigned(): boolean {
    if (this.chatStore.chatRooms.length === 0 || this.userStore.getUserIsTherapist) {
      return false;
    }

    return this._selectChat(this.chatStore.chatRooms[0].id);
  }

  private _resolveAssigmentsUserAdded(): boolean {
    AppRouter.push({ name: APP_CHAT_ROUTES.assignments.root.name });

    return true;
  }

  private _resolveScheduleReminder(): boolean {
    AppRouter.push({ name: APP_SCHEDULE_ROUTES.root.name });

    return true;
  }

  private _getChatId(payload: NOTIFICATION_DATA): undefined | string {
    if (!hasValue(payload)) return undefined;
    if (isObject(payload) && isValidString(payload.chat)) return payload.chat;
    if (isObject(payload) && isValidString(payload["thread-id"])) return payload["thread-id"];
    if (isObject(payload) && isObject(payload.aps) && isValidString(payload.aps["thread-id"])) {
      return payload.aps["thread-id"];
    }

    return undefined;
  }
}

export default PerformNotificationActionHandler;
